import React, { useState, useEffect } from 'react';
import FileUpload from './FileUpload';
import api from '../services/api';

const PopupManager = ({ isAuthenticated, onAuthRequired }) => {
  const [popup, setPopup] = useState({
    title: '',
    content: '',
    image_url: '',
    link_url: '',
    link_text: '',
    link_target: '_self',
    delay_seconds: 1,
    duration_seconds: 0,
    is_active: true,
    use_cookie: true
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Load existing popup on component mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchPopup();
    }
  }, [isAuthenticated]);

  const fetchPopup = async () => {
    try {
      const response = await api.get('/post-publisher/v1/popup');
      if (response.data) {
        setPopup(response.data);
      }
    } catch (error) {
      console.error('Error fetching popup:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPopup(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/post-publisher/v1/media', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data) {
        setPopup(prev => ({
          ...prev,
          image_url: response.data.source_url
        }));
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      onAuthRequired();
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post('/post-publisher/v1/popup', popup);

      if (response.data) {
        setShowSuccessModal(true);
        setPopup(response.data);
        
        // Auto-close modal after 3 seconds (like other success modals)
        setTimeout(() => {
          setShowSuccessModal(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Error saving popup:', error);
      alert('Error saving popup');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Popup Management</h2>
              <p className="text-gray-600">Create and manage popup notifications for your website visitors.</p>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium text-gray-700">Status:</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={popup.is_active}
                  onChange={(e) => setPopup(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                <span className="ml-3 text-sm font-medium text-gray-900">
                  {popup.is_active ? 'Active' : 'Inactive'}
                </span>
              </label>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Popup Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={popup.title}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter popup title"
            />
          </div>

          {/* Content */}
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
              Popup Content
            </label>
            <textarea
              id="content"
              name="content"
              value={popup.content}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter popup content (optional)"
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Popup Image
            </label>
            <FileUpload
              onFileSelect={handleImageUpload}
              accept="image/*"
              maxSize={5 * 1024 * 1024} // 5MB
              label="Upload popup image"
              className="mb-2"
            />
            {popup.image_url && (
              <div className="mt-2">
                <img
                  src={popup.image_url}
                  alt="Popup preview"
                  className="max-w-xs h-32 object-cover rounded-lg border"
                />
              </div>
            )}
          </div>

          {/* Link URL */}
          <div>
            <label htmlFor="link_url" className="block text-sm font-medium text-gray-700 mb-2">
              Link URL
            </label>
            <input
              type="url"
              id="link_url"
              name="link_url"
              value={popup.link_url}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="https://example.com"
            />
          </div>

          {/* Link Text */}
          <div>
            <label htmlFor="link_text" className="block text-sm font-medium text-gray-700 mb-2">
              Link Text
            </label>
            <input
              type="text"
              id="link_text"
              name="link_text"
              value={popup.link_text}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Click Here"
            />
          </div>

          {/* Link Target */}
          <div>
            <label htmlFor="link_target" className="block text-sm font-medium text-gray-700 mb-2">
              Link Target
            </label>
            <select
              id="link_target"
              name="link_target"
              value={popup.link_target}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="_self">Same Window</option>
              <option value="_blank">New Window</option>
            </select>
          </div>

          {/* Delay and Duration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="delay_seconds" className="block text-sm font-medium text-gray-700 mb-2">
                Delay (seconds)
              </label>
              <input
                type="number"
                id="delay_seconds"
                name="delay_seconds"
                value={popup.delay_seconds}
                onChange={handleInputChange}
                min="0"
                max="60"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">How many seconds to wait before showing popup</p>
            </div>

            <div>
              <label htmlFor="duration_seconds" className="block text-sm font-medium text-gray-700 mb-2">
                Duration (seconds)
              </label>
              <input
                type="number"
                id="duration_seconds"
                name="duration_seconds"
                value={popup.duration_seconds}
                onChange={handleInputChange}
                min="0"
                max="300"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">How long to show popup (0 = until manually closed)</p>
            </div>
          </div>

          {/* Checkboxes */}
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="use_cookie"
                name="use_cookie"
                checked={popup.use_cookie}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="use_cookie" className="ml-2 block text-sm text-gray-700">
                Remember if user closes popup (1 hour)
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Saving...' : 'Save Popup'}
            </button>
          </div>
        </form>

        {/* Usage Instructions */}
        <div className="mt-8 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-sm font-medium text-gray-900 mb-2">Usage Instructions</h3>
          <p className="text-sm text-gray-600 mb-2">
            To display the popup on your website, add the following shortcode to any page or post:
          </p>
          <code className="text-sm bg-white px-2 py-1 rounded border">[popup]</code>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-2">Success!</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Popup has been saved successfully.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PopupManager;
